import React, { useEffect, useRef, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useViewSync } from '../../contexts/ViewSyncContext';
import useViewer3dModels from '../../hooks/useViewer3dModels';
import useViewer3dView from '../../hooks/useViewer3dView';
import { useProjectTerrain } from '../../hooks/useTerrainSettings';
import ViewSyncToggle from '../ViewSyncToggle';
import Viewer3DScene from '../Viewer3DScene';
import TerrainControls3D from '../terrain/TerrainControls3D';
import { viewer3dPath } from '../../lib/map-session';

/**
 * Docked "3D view" panel of the map workspace (lib/panel-registry.js `viewer3d`): the same scene as
 * the /3d page (components/Viewer3DScene.js, hooks/useViewer3dModels.js) beside the map, for the
 * map's ?project= / ?dataset= / ?model=. Sync camera lives here only: with it on, the map centre
 * and the 3D orbit target follow each other (a map target far from the models fits the models).
 *
 * The Canvas is mounted only while the panel is on screen: closing it, or hiding it behind another
 * dock tab, unmounts it and releases its WebGL context.
 */
export default function Viewer3DPanel({ projectId = null, datasetId = null, modelId = null, navigate }) {
  const { C } = useTheme();
  const { syncCamera, setSyncCamera } = useViewSync();
  useProjectTerrain(projectId);
  const view = useViewer3dView();
  const { fenceModels, shownModels, storedLoading, loaded, total, datasets, fitModelIds } = useViewer3dModels({ projectId, datasetId, modelId });
  const { showFences, setShowFences, terrainReady } = view;

  // Sync only makes sense while this panel is open
  useEffect(() => () => setSyncCamera(false), [setSyncCamera]);

  // Dock tabs stay mounted behind the front one (display: none): draw the scene only when visible
  const hostRef = useRef(null);
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const el = hostRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setVisible(el.offsetWidth > 0 && el.offsetHeight > 0));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const [showControls, setShowControls] = useState(false);
  const small = { fontSize: 11, color: C.text2 };

  return (
    <div data-testid="viewer3d-panel" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, background: C.bg2 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', borderBottom: `1px solid ${C.border}`, flexShrink: 0, flexWrap: 'wrap' }}>
        <ViewSyncToggle syncCamera={syncCamera} setSyncCamera={setSyncCamera} />
        <label style={{ ...small, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }} data-testid="viewer3d-panel-models">
          <input type="checkbox" checked={showFences} onChange={(e) => setShowFences(e.target.checked)} />
          Models ({shownModels.length} of {fenceModels.length} drawn){storedLoading ? (total > 0 ? ` · Loading models ${Math.min(loaded + 1, total)} of ${total}…` : ' · Loading models…') : ''}
        </label>
        <button type="button" onClick={() => setShowControls((v) => !v)} aria-expanded={showControls}
          style={{ ...small, background: 'none', border: `1px solid ${C.border}`, borderRadius: 4, padding: '1px 6px', cursor: 'pointer' }}>
          Terrain
        </button>
        <button type="button" title="Open the full 3D viewer page" aria-label="Open the full 3D viewer page"
          onClick={() => navigate?.(viewer3dPath(projectId, { dataset: datasetId, model: modelId }))}
          style={{ marginLeft: 'auto', background: 'none', border: 'none', color: C.text2, cursor: 'pointer', display: 'flex', padding: 2 }}>
          <ExternalLink size={13} />
        </button>
      </div>
      {showControls && (
        <div style={{ padding: 8, borderBottom: `1px solid ${C.border}`, flexShrink: 0, maxHeight: '40%', overflow: 'auto' }}>
          <TerrainControls3D C={C} ready={terrainReady} />
        </div>
      )}
      <div ref={hostRef} style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {visible ? (
          <Viewer3DScene view={view} datasets={datasets} fenceModels={fenceModels} fitModelIds={fitModelIds} />
        ) : null}
      </div>
    </div>
  );
}
